import { CSSObject } from '@emotion/react';
import * as React from 'react';
import { useEventCallback } from '../../../hooks';
import { DialogBackdropColor, useHoneycombTheme } from '../../../theming';
import { alpha, FixMeLater } from '../../../utils';
import { Styler } from '../../Styler/Styler';
import { useDialogContext } from '../Dialog.context';
import { DIALOG_STATIC_STYLES } from '../Dialog.styles';

const getBackdropColor = (color: DialogBackdropColor): string => {
    if (color === 'white') {
        return '#FFF';
    }
    return '#000';
};

export type DialogBackdropProps = {
    /**
     * The background color of the backdrop.
     * @default black
     */
    color?: DialogBackdropColor;

    /**
     * Determines if a blur effect is applied to the backdrop.
     * @default true
     */
    blur?: boolean;
};

export const DialogBackdrop = React.forwardRef(function DialogContent(
    props: DialogBackdropProps,
    ref: React.ForwardedRef<FixMeLater>
): React.JSX.Element {
    const { color = 'black', blur: blurProp = true, ...other } = props;

    const { handleOpenChange, loading, transitionStatus, dismissable, withAnimation } = useDialogContext();

    const { theme } = useHoneycombTheme();
    const dialogTheme = theme.components.themes.Dialog;
    const { opacity, blur } = dialogTheme.backdrop;

    const cssVariables: CSSObject = React.useMemo(() => {
        return {
            '--dialog-backdrop-color': alpha(getBackdropColor(color), opacity),
            '--dialog-backdrop-filter': blurProp ? `blur(${blur}px)` : undefined,
        };
    }, [blur, blurProp, color, opacity]);

    const handleBackdropPress = useEventCallback((_event: React.SyntheticEvent) => {
        handleOpenChange?.(false, undefined, 'backdrop-pressed');
    });

    return (
        <Styler
            ref={ref}
            style={cssVariables}
            css={DIALOG_STATIC_STYLES.backdrop}
            aria-hidden={true}
            data-transition-status={loading ? 'loading' : transitionStatus}
            data-animation-disabled={!withAnimation || undefined}
            data-dialog-backdrop={true}
            data-blur={blurProp || undefined}
            onMouseDown={dismissable ? handleBackdropPress : undefined}
            {...other}
        />
    );
});
